import { useParams } from 'react-router'
import { isProd } from '@/config'
import useUser from './useUser'

type FeatureFlagAccess = 'none' | 'stampede-only' | 'all'

interface FeatureFlag {
  enabled: boolean
  access: FeatureFlagAccess
}

const NEW_UI_STORAGE_KEY = 'connect:newUi:enabled'

export function setNewUiEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  if (enabled) {
    window.localStorage.setItem(NEW_UI_STORAGE_KEY, 'true')
  } else {
    window.localStorage.removeItem(NEW_UI_STORAGE_KEY)
  }
  window.location.reload()
}

export function isNewUiEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(NEW_UI_STORAGE_KEY) === 'true'
}

/**
 * Orgs whose users (any email) get the waitlist, alongside Stampede staff.
 * Turning the waitlist on for a client = add their org_id here, then set
 * `waitlist_enabled` on each of their venues' config.
 */
const WAITLIST_ORG_ALLOWLIST = new Set<string>([
  '2cda782d-a5f8-4ecd-a889-110a2bf565cd',
])

export function useFeatureFlags() {
  const user = useUser()
  const { org_id } = useParams<{ org_id: string }>()

  /**
   * Determines the AI email templates feature flag based on the user's email.
   *
   * @returns \{FeatureFlag\} The feature flag object indicating whether the feature is enabled
   * and the level of access. The feature is enabled and access is set to 'stampede-only'
   * if the user's email ends with '\@stampede.ai' (case insensitive).
   */
  const getAiEmailTemplatesFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  const getDeleteSegmentDataFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Audiences (New) v2 rebuild. Gates the tailwind-v4 rebuild of the
   * "Audiences (New)" list + full-page create/edit builder
   * (`marketing/audiences`, `audiences/new`, `audiences/:id`).
   *
   * CUT OVER (Aug 2026) — now on for everyone. It shipped dark to
   * `@stampede.ai` addresses only, which meant every customer was still being
   * served the legacy Josef list, and the builder routes bounced them straight
   * back to it. The flag is kept rather than deleted so this is a one-line
   * revert if the rebuild misbehaves in the wild; the legacy files stay in
   * place behind it until they are deliberately removed.
   *
   * Orthogonal to the org-level `morpheus_simple_segments` data-provider gate,
   * which decides whether the nav entry shows at all.
   */
  const getAudiencesV2Flag = (): FeatureFlag => {
    return {
      enabled: true,
      access: 'all',
    }
  }

  /**
   * Determines the workflow logs feature flag based on the user's email.
   *
   * @returns \{FeatureFlag\} The feature flag object indicating whether the feature is enabled
   * and the level of access. The feature is enabled and access is set to 'stampede-only'
   * if the user's email ends with '\@stampede.ai' (case insensitive).
   */
  const getWorkflowLogsFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Determines the brand kit generation feature flag based on the user's email.
   * When enabled, users can generate brand kits from a website URL using SSE.
   * When disabled, users can only create brand kits with a name.
   */
  const getBrandKitGenerationFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Determines the agent mode (AI chat) feature flag based on the user's email.
   * Only available to stampede.ai users and allowed external emails.
   */
  const getAgentModeFlag = (): FeatureFlag => {
    const email = user?.email?.toLowerCase() ?? ''
    const isStampede = email.endsWith('@stampede.ai')
    const allowedEmails = [
      'ed@arthospitality.co.uk',
      'liam@arthospitality.co.uk',
      'john@oir-group.com',
      'matthew@sankeys.co.uk',
      'louis@howies.uk.com',
    ]

    const enabled = isStampede || allowedEmails.includes(email)
    return {
      enabled,
      access: isStampede ? 'stampede-only' : enabled ? 'all' : 'none',
    }
  }

  /**
   * Determines the reviews AI analytics feature flag based on the user's email.
   * Only available to stampede.ai users.
   */
  const getReviewsAiAnalyticsFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Mobile floor-plan layout authoring. Adds a "Mobile" toggle to the
   * floor-plans-edit canvas so venues can hand-position tables for
   * phone screens, separate from the desktop layout. Gated to
   * stampede-only until the zion backend ships the `layout_kind`
   * column on FloorPlanTables.
   */
  const getMobileFloorPlanLayoutFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Waitlist (Feature A). Host-side waitlist sheet. On for Stampede staff
   * everywhere (production trial) and for every user of an allowlisted org;
   * the day-planner header only surfaces the Waitlist button when this is
   * enabled (and the date is today). The venue still needs
   * `waitlist_enabled` on its config or adds are refused.
   */
  const getWaitlistFlag = (): FeatureFlag => {
    const isStaff = user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const isAllowlistedOrg = Boolean(
      org_id && WAITLIST_ORG_ALLOWLIST.has(org_id)
    )
    if (isAllowlistedOrg) return { enabled: true, access: 'all' }
    return { enabled: isStaff, access: isStaff ? 'stampede-only' : 'none' }
  }

  /**
   * Day-planner grid-engine switch (grid-engine PRD D2). Gates the "New grid"
   * toggle in the day-planner-fast header that swaps the legacy custom grid
   * for the tailwind-v4 day-planner block grid. Stampede-only while the v3
   * engine is brought to parity; widened at Josef's say-so.
   */
  const getNewDayPlannerGridFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * List View 3 (`bookings/list-view-3`). The tailwind-v4 rebuild of the
   * bookings List View, completing the `-3` family. Stampede-only while it's
   * validated in parallel with `list-view-fast`; the sidebar + org-mirror gate
   * route to list-view-3 (flag on) or list-view-fast (flag off), and widen to
   * `all` at cutover (ARD §9 / PRD D17).
   */
  const getListViewV3Flag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Venue Music DEMO mode (bookings header Music button). This does NOT gate
   * the real feature — a venue with SnapGuard kit and an exposed speaker gets
   * the real player with no flag at all. When the capability gate fails
   * (no SnapGuard / no speakers), Stampede staff get a fabricated, locally
   * interactive demo player instead of nothing, so the surface can be shown
   * on any venue. Everyone else sees no button.
   */
  const getVenueMusicDemoFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Template issues warning in the email campaign/workflow editor. Surfaces
   * known Unlayer template compatibility problems (see
   * `TemplateIssueService`) to stampede staff only while the check list is
   * still being built out.
   */
  const getTemplateIssuesFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Knowledge Base panel toggle (top-right of the nav, both UIs). Stampede
   * staff only for now, same as the Knowledge Base nav entry's own
   * `internal.staff` capability gate (nav-v4/config.ts) — content is still
   * under review. Remove once the feature is ready for everyone.
   */
  const getKnowledgeBasePanelFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * Lets stampede staff dismiss the `SubscriptionRequiredNotice` full-page
   * lockout (org has no subscription record) and see the app underneath
   * anyway — needed to debug/support an org in that state without losing
   * app access themselves. Never available to customers.
   */
  const getSubscriptionLockoutOverrideFlag = (): FeatureFlag => {
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    const enabled = emailCheck
    return {
      enabled,
      access: enabled ? 'stampede-only' : 'none',
    }
  }

  /**
   * WhatsApp marketplace app (Embedded Signup via cypher). Stampede staff only
   * until the Meta app is live, and never in production (WhatsApp isn't set up
   * there yet); widen to `all` and drop the `isProd()` check then.
   */
  const getWhatsAppFlag = (): FeatureFlag => {
    if (isProd()) return { enabled: false, access: 'none' }
    const emailCheck =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    return {
      enabled: emailCheck,
      access: emailCheck ? 'stampede-only' : 'none',
    }
  }

  /**
   * Master flag for the Connect v4 UI overhaul. Stampede staff only; each staff
   * member opts in per-browser via localStorage. Non-stampede users always see
   * the old UI. Toggle with `setNewUiEnabled(true|false)`.
   */
  const getNewUiFlag = (): FeatureFlag => {
    const isStampede =
      user?.email?.toLowerCase().endsWith('@stampede.ai') ?? false
    if (!isStampede) {
      return { enabled: false, access: 'none' }
    }
    return {
      enabled: isNewUiEnabled(),
      access: 'stampede-only',
    }
  }

  return {
    aiEmailTemplates: getAiEmailTemplatesFlag(),
    deleteSegmentData: getDeleteSegmentDataFlag(),
    audiencesV2: getAudiencesV2Flag(),
    workflowLogs: getWorkflowLogsFlag(),
    brandKitGeneration: getBrandKitGenerationFlag(),
    agentMode: getAgentModeFlag(),
    reviewsAiAnalytics: getReviewsAiAnalyticsFlag(),
    mobileFloorPlanLayout: getMobileFloorPlanLayoutFlag(),
    waitlist: getWaitlistFlag(),
    newDayPlannerGrid: getNewDayPlannerGridFlag(),
    listViewV3: getListViewV3Flag(),
    venueMusicDemo: getVenueMusicDemoFlag(),
    templateIssues: getTemplateIssuesFlag(),
    knowledgeBasePanel: getKnowledgeBasePanelFlag(),
    subscriptionLockoutOverride: getSubscriptionLockoutOverrideFlag(),
    whatsApp: getWhatsAppFlag(),
    newUi: getNewUiFlag(),
  }
}
